// ui/presenters/billingExportPresenter.js
import { todayDateStr } from '../../domain/services/effectiveDating';

/**
 * What goes into the spreadsheet a biller re-keys from, what it is called, and what it covered.
 *
 * THE TABLE SAYS WHICH COLUMNS ARE SHOWING AND IN WHAT ORDER; this decides what the file holds. A
 * column is read the same way on screen and in the file, because both use the page's one column
 * list, so the two cannot disagree about what a row says.
 */

/**
 * The file's columns: the ones showing, in the biller's order, then every hidden one in the page's.
 *
 * EVERY COLUMN, NOT JUST THE SHOWING ONES, because the file is stamped as exported. A biller who
 * took it with too few columns showing would otherwise leave items marked exported with no record
 * of what went out with them. The chosen order still leads, since that is the order somebody keys from.
 *
 * @param {Object[]} visibleColumns - what the table reports as showing, in order
 * @param {Object[]} allColumns - the page's column list
 * @returns {Object[]}
 */
export function exportColumns(visibleColumns, allColumns) {
  const showing = new Set(visibleColumns.map((column) => column.name));

  return visibleColumns.concat(allColumns.filter((column) => !showing.has(column.name) && !column.preventExport));
} // exportColumns()

/** Only what will actually bill. A blocked row has nothing to key. */
export function exportableRows(rows) {
  return rows.filter((row) => !row.isHeld);
} // exportableRows()

/**
 * What the file covered, for the audit record.
 *
 * READ OFF THE ROWS, NOT OFF THE FILTERS THAT PRODUCED THEM. The table does not report what its
 * filters are set to, and the rows are the better answer anyway: a filter says what somebody asked
 * for, and this says what actually left the system. An export record that carries neither can only
 * be matched back to a file by the timestamp on the items it stamped.
 *
 * `rowsInFile` COUNTS THE BLOCKED ROWS TOO, and is the only place they are counted. A blocked row
 * is in the file and is deliberately not stamped, so every other number on the record — the ids
 * sent, the stamped count — describes the billable subset rather than the file somebody received.
 */
export function describeExport(rows) {
  const serviceDates = rows.map((row) => row.serviceDate).filter(Boolean).sort();
  const payerNames = [...new Set(rows.map((row) => row.payerName).filter(Boolean))].sort();

  return {
    serviceDateFrom: serviceDates[0] ?? null,
    serviceDateTo: serviceDates[serviceDates.length - 1] ?? null,
    payerNames,
    rowsInFile: rows.length,
  };
} // describeExport()

/**
 * Named for what it is and the day it was taken.
 *
 * NOT THE PERIOD IT COVERS, which it no longer knows: the period is a filter on the table like any
 * other, and the table does not say what its filters are set to. The day the file was produced is
 * what a biller looking through a downloads folder actually remembers.
 *
 * THE LOCAL CALENDAR DAY, which is what `todayDateStr` is for. A UTC date names a file taken on a
 * September evening in California for the 13th when the biller took it on the 12th.
 *
 * NO EXTENSION: the table appends one, because the user chooses the format.
 */
export function billingExportName(today = todayDateStr()) {
  return `billing-items-${today}`;
} // billingExportName()
